import { ListTree } from "lucide-react";
import { useEffect, useId, useLayoutEffect, useRef, useState, type RefObject } from "react";
import { useTranslation } from "react-i18next";
import { ConversationTimeline } from "@/components/conversation-timeline";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import type { useConversationNavigation } from "@/hooks/use-conversation-navigation";
import type { ConversationTarget } from "@/lib/conversation-target";
import { cn } from "@/lib/utils";

type TimelineProps = {
  sessionId: string;
  navigation: ReturnType<typeof useConversationNavigation>;
  onNavigate: (target: ConversationTarget) => void;
  onOpenThread: (threadId: string) => void;
};

type ScrollSnapshot = {
  height: number;
  viewportHeight: number;
  top: number;
  pinned: boolean;
  turnIds: string[];
};

const atBottom = (element: HTMLElement) =>
  element.scrollHeight - element.clientHeight - element.scrollTop <= 2;

function TimelineRail({ sessionId, navigation, onNavigate, onOpenThread }: TimelineProps) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const railRef = useRef<HTMLElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const measured = useRef<ScrollSnapshot | null>(null);
  const pointRef = useRef<HTMLButtonElement | null>(null);
  const hovered = useRef(false);
  const pinned = useRef(false);
  const following = useRef(false);
  const followTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const restoringFocus = useRef(false);
  const panelId = useId();
  const degraded = navigation.capabilityState !== "supported" || !navigation.indexSupported
    || navigation.indexState !== "ready" || navigation.indexError || Boolean(navigation.childrenError);
  const loading = navigation.capabilityState === "pending" || navigation.loadingChildren || navigation.loadingOlderTurns;
  const turnIds = navigation.turns.map((turn) => turn.turnId);

  // Save the last laid-out dimensions, then compare them with the new DOM before
  // paint. This also puts the clipped rail at the bottom on its first render.
  useLayoutEffect(() => {
    const element = scrollRef.current;
    if (!element) return;
    const previous = measured.current;
    const oldStart = previous?.turnIds.length ? turnIds.indexOf(previous.turnIds[0]) : -1;
    const prepended = previous && oldStart > 0
      && previous.turnIds.every((id, index) => turnIds[oldStart + index] === id);
    if (!previous || (previous.pinned && (element.scrollHeight !== previous.height || element.clientHeight !== previous.viewportHeight))) {
      element.scrollTop = Math.max(0, element.scrollHeight - element.clientHeight);
    } else if (prepended) {
      element.scrollTop = Math.max(0, previous.top + element.scrollHeight - previous.height);
    }
    measured.current = {
      height: element.scrollHeight,
      viewportHeight: element.clientHeight,
      top: element.scrollTop,
      pinned: !previous ? true : previous.pinned && atBottom(element),
      turnIds,
    };
  });

  // A details toggle changes height without a React render. Observe the same scroll
  // container's content and keep the bottom visible only while the user is pinned.
  useLayoutEffect(() => {
    const element = scrollRef.current;
    const content = element?.firstElementChild;
    if (!element || !content || typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => {
      const previous = measured.current;
      if (!previous) return;
      if (previous.pinned && (element.scrollHeight !== previous.height || element.clientHeight !== previous.viewportHeight)) {
        element.scrollTop = Math.max(0, element.scrollHeight - element.clientHeight);
      }
      measured.current = { ...previous, height: element.scrollHeight, viewportHeight: element.clientHeight, top: element.scrollTop };
    });
    observer.observe(content);
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const activeKey = navigation.activeTarget ? JSON.stringify(navigation.activeTarget) : null;
  // A toggle rechecks visibility after the header appears/disappears; it only
  // scrolls if the active target is actually outside the comfortable viewport.
  useEffect(() => {
    const element = scrollRef.current;
    if (!activeKey || !element) return;
    const row = element.querySelector<HTMLElement>('[data-timeline-active="true"]');
    if (!row) return;
    // In compact mode the dot is the visible part of this same grid row.
    const target = (expanded
      ? row.querySelector<HTMLElement>('a[aria-current="location"], details button[aria-current="location"]')
        ?? row.querySelector<HTMLElement>("summary a")
      : row.querySelector<HTMLElement>('button[aria-current="location"]')) ?? row;
    const viewport = element.getBoundingClientRect();
    const bounds = target.getBoundingClientRect();
    const header = element.querySelector<HTMLElement>(".sticky");
    const margin = 24;
    const visibleTop = viewport.top + (header?.getBoundingClientRect().height ?? 0) + margin;
    const visibleBottom = viewport.bottom - margin;
    if (bounds.top >= visibleTop && bounds.bottom <= visibleBottom) return;
    const delta = bounds.top < visibleTop ? bounds.top - visibleTop : bounds.bottom - visibleBottom;
    const top = Math.max(0, element.scrollTop + delta);
    if (Math.abs(top - element.scrollTop) < 1) return;
    // Following the transcript temporarily releases bottom pinning. Rail scroll
    // events are local to this container and never arm the transcript spy.
    following.current = true;
    if (measured.current) measured.current = { ...measured.current, pinned: false, top };
    if (followTimer.current) clearTimeout(followTimer.current);
    followTimer.current = setTimeout(() => {
      following.current = false;
      if (measured.current && scrollRef.current) measured.current = {
        ...measured.current, top: scrollRef.current.scrollTop, pinned: atBottom(scrollRef.current),
      };
      followTimer.current = null;
    }, 500);
    const behavior = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth";
    if (element.scrollTo) element.scrollTo({ top, behavior });
    else element.scrollTop = top;
  }, [activeKey, expanded]);

  useEffect(() => () => { if (followTimer.current) clearTimeout(followTimer.current); }, []);

  const collapse = () => {
    pinned.current = false;
    hovered.current = false;
    // Return focus before hiding the overlay, without reopening it via onFocus.
    if (railRef.current?.contains(document.activeElement)) {
      restoringFocus.current = true;
      (railRef.current.querySelector<HTMLButtonElement>('[data-timeline-active="true"] button')
        ?? pointRef.current)?.focus();
      restoringFocus.current = false;
    }
    setExpanded(false);
  };

  useEffect(() => {
    if (!expanded) return;
    const outside = (event: PointerEvent) => {
      if (!railRef.current?.contains(event.target as Node)) collapse();
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        event.stopPropagation();
        collapse();
      }
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [expanded]);

  return (
    <aside
      ref={railRef}
      aria-label={t("navigation.timelineLabel")}
      className="conversation-timeline-theme relative w-10 min-h-0 shrink-0 text-foreground"
      data-testid="conversation-timeline-rail"
      data-state={expanded ? "expanded" : "collapsed"}
      onPointerEnter={(event) => {
        if (event.pointerType === "touch") return;
        hovered.current = true;
        setExpanded(true);
      }}
      onPointerLeave={() => {
        hovered.current = false;
        if (!pinned.current && !railRef.current?.contains(document.activeElement)) setExpanded(false);
      }}
      onFocus={() => { if (!restoringFocus.current) setExpanded(true); }}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node | null) && !hovered.current && !pinned.current) setExpanded(false);
      }}
    >
      <div
        ref={scrollRef}
        onWheel={() => { following.current = false; }}
        onTouchMove={() => { following.current = false; }}
        onPointerDown={() => { following.current = false; }}
        onScroll={(event) => {
          const element = event.currentTarget;
          const previous = measured.current;
          // A layout change may fire scroll before ResizeObserver; retain the old
          // pinned state until the observer has applied the height change.
          if (previous && element.scrollHeight === previous.height && element.clientHeight === previous.viewportHeight) {
            measured.current = { ...previous, top: element.scrollTop, pinned: following.current ? false : atBottom(element) };
          }
        }}
        className={cn(
          "absolute inset-y-0 right-0 z-30 w-[22.5rem] overflow-y-auto overflow-x-hidden border-l border-border bg-background transition-[clip-path] duration-200 ease-out motion-reduce:transition-none [overflow-anchor:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden",
          expanded ? "[clip-path:inset(0_0_0_0)]" : "[clip-path:inset(0_0_0_20rem)]",
        )}
      >
        <ConversationTimeline
          sessionId={sessionId}
          navigation={navigation}
          onNavigate={(target) => { collapse(); onNavigate(target); }}
          onOpenThread={(threadId) => { collapse(); onOpenThread(threadId); }}
          rail={{
            expanded,
            panelId,
            renderPoint: (turn, position, active) => (
              <div className={cn("relative col-start-2 row-start-1 flex justify-center", !turn && "pt-6")}>
                <button
                  type="button"
                  ref={(element) => { if (position === Math.max(0, navigation.turns.length - 1) && element) pointRef.current = element; }}
                  className={cn("relative -mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-[3px] hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring", active && "bg-accent ring-1 ring-inset ring-border")}
                  aria-label={turn ? t("navigation.turnPoint", { count: turn.ordinal ?? position + 1 }) : t("navigation.openTimeline")}
                  aria-current={active ? "location" : undefined}
                  aria-expanded={expanded}
                  aria-controls={panelId}
                  onClick={() => {
                    pinned.current = true;
                    setExpanded(true);
                  }}
                >
                  <span aria-hidden="true" className={cn(
                    "size-2 rounded-full border border-current bg-background",
                    active ? "size-3 bg-foreground text-foreground ring-2 ring-background" : degraded || turn?.pending ? "text-muted-foreground" : "bg-foreground text-foreground",
                    loading && "animate-pulse motion-reduce:animate-none",
                  )} />
                </button>
              </div>
            ),
          }}
        />
      </div>
    </aside>
  );
}

export function ConversationTimelinePanel({
  variant,
  sessionId,
  navigation,
  onNavigate,
  onOpenThread,
  open: controlledOpen,
  onOpenChange,
  hideTrigger = false,
  returnFocusRef,
}: TimelineProps & {
  variant: "rail" | "dialog";
  /** Allows the mobile overflow menu to open the dialog without a header icon. */
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideTrigger?: boolean;
  returnFocusRef?: RefObject<HTMLButtonElement>;
}) {
  const { t } = useTranslation();
  const [internalOpen, setInternalOpen] = useState(false);
  const open = controlledOpen ?? internalOpen;
  const setOpen = (value: boolean) => { setInternalOpen(value); onOpenChange?.(value); };
  if (variant === "rail") {
    return <TimelineRail key={sessionId} sessionId={sessionId} navigation={navigation} onNavigate={onNavigate} onOpenThread={onOpenThread} />;
  }
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      {!hideTrigger && <DialogTrigger asChild>
        <Button variant="ghost" size="icon" className="conversation-timeline-theme size-7 shrink-0 rounded-[3px] text-foreground" aria-label={t("navigation.openTimeline")} title={t("navigation.openTimeline")}>
          <ListTree className="size-4" aria-hidden="true" />
        </Button>
      </DialogTrigger>}
      <DialogContent onCloseAutoFocus={hideTrigger && returnFocusRef ? (event) => {
        event.preventDefault();
        returnFocusRef.current?.focus();
      } : undefined} className="conversation-timeline-theme inset-x-0 bottom-0 top-auto h-[85dvh] max-h-[85dvh] w-full max-w-full translate-x-0 translate-y-0 rounded-t-lg p-0 text-foreground [&>button]:rounded-[3px] sm:left-1/2 sm:top-1/2 sm:h-auto sm:max-w-md sm:-translate-x-1/2 sm:-translate-y-1/2 sm:rounded-lg">
        <DialogTitle className="sr-only">{t("navigation.timeline")}</DialogTitle>
        <DialogDescription className="sr-only">{t("navigation.timelineDescription")}</DialogDescription>
        <div className="h-full max-h-[85dvh] overflow-y-auto pt-4">
          <ConversationTimeline
            sessionId={sessionId}
            navigation={navigation}
            onNavigate={(target) => { setOpen(false); onNavigate(target); }}
            onOpenThread={(threadId) => { setOpen(false); onOpenThread(threadId); }}
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
